if (!customElements.get('ua-fragrance-comparison')) {
  customElements.define('ua-fragrance-comparison', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const data = JSON.parse(this.querySelector('[data-comparison-data]').textContent);
      this.products = new Map(data.products.map(product => [product.handle, product]));
      this.labels = data.labels;
      this.selected = [];
      this.storageKey = 'ua-fragrance-comparison-v1';
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || '[]');
        if (Array.isArray(saved)) this.selected = [...new Set(saved)].filter(handle => this.products.has(handle)).slice(0, 3);
      } catch {}
      this.dialog = this.querySelector('dialog');
      this.table = this.querySelector('[data-comparison-table]');
      this.tray = this.querySelector('.ua-compare-tray');
      this.status = this.querySelector('[data-comparison-status]');
      this.modalStatus = this.querySelector('[data-comparison-modal-status]');
      this.scroller = this.querySelector('.ua-compare-table-scroll');
      this.scroller.addEventListener('scroll', () => this.updateScrollControls(), { passive: true });
      this.resizeObserver = new ResizeObserver(() => this.updateScrollControls());
      this.resizeObserver.observe(this.scroller);
      this.documentClick = event => {
        const button = event.target.closest('[data-compare-handle]');
        if (!button) return;
        event.preventDefault();
        this.toggle(button.dataset.compareHandle);
      };
      document.addEventListener('click', this.documentClick);
      this.addEventListener('click', event => {
        const scrollButton = event.target.closest('[data-comparison-scroll]');
        if (scrollButton) {
          const scroller = this.querySelector('.ua-compare-table-scroll');
          const firstProduct = this.table.querySelector('thead th:nth-child(2)');
          if (firstProduct) scroller.scrollBy({left: Number(scrollButton.dataset.comparisonScroll) * firstProduct.getBoundingClientRect().width, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
        }
        const remove = event.target.closest('[data-comparison-remove]');
        if (remove) {
          const wasOpen = this.dialog.open;
          const handle = remove.dataset.comparisonRemove;
          this.focusReturnHandle = handle;
          this.toggle(handle);
          if (wasOpen && this.dialog.open) this.querySelector('[data-comparison-close]').focus();
          else if (!wasOpen) this.focusSelection(handle);
        }
        if (event.target.closest('[data-comparison-clear]')) {
          const handle = this.selected[0];
          this.focusReturnHandle = handle;
          this.selected = [];
          this.update();
          this.announce(this.labels.cleared);
          if (!this.dialog.open) this.focusSelection(handle);
        }
        if (event.target.closest('[data-comparison-open]') && this.selected.length > 1) {
          this.lastTrigger = event.target.closest('button');
          this.focusReturnHandle = null;
          this.renderTable();
          this.dialog.showModal();
          this.scroller.scrollLeft = 0;
          this.scroller.scrollTop = 0;
          this.updateScrollControls();
          document.body.classList.add('ua-compare-modal-open');
          this.querySelector('[data-comparison-close]').focus();
        }
        if (event.target.closest('[data-comparison-close]')) this.dialog.close();
        if (event.target === this.dialog) {
          const box = this.dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
        }
      });
      this.dialog.addEventListener('close', () => {
        document.body.classList.remove('ua-compare-modal-open');
        if (this.lastTrigger?.isConnected && !this.lastTrigger.disabled && this.lastTrigger.getClientRects().length) this.lastTrigger.focus();
        else this.focusSelection(this.focusReturnHandle);
      });
      this.observer = new MutationObserver(() => this.syncButtons());
      const grid = document.querySelector('main');
      if (grid) this.observer.observe(grid, { childList: true, subtree: true });
      this.update();
    }
    disconnectedCallback() {
      document.removeEventListener('click', this.documentClick);
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      document.body.classList.remove('ua-has-comparison', 'ua-compare-modal-open');
    }
    element(tag, className, text) {
      const element = document.createElement(tag);
      if (className) element.className = className;
      if (text != null) element.textContent = text;
      return element;
    }
    focusSelection(handle) {
      const available = element => element.isConnected && !element.disabled && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden';
      const chips = [...this.tray.querySelectorAll('[data-comparison-remove]')];
      const buttons = [...document.querySelectorAll('[data-compare-handle]')];
      const target = chips.find(available)
        || buttons.find(button => button.dataset.compareHandle === handle && available(button))
        || buttons.find(available);
      target?.focus();
    }
    announce(text) {
      const status = this.dialog.open ? this.modalStatus : this.status;
      const inactive = this.dialog.open ? this.status : this.modalStatus;
      inactive.textContent = '';
      status.textContent = text;
    }
    label(key, values) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll('{{ ' + name + ' }}', String(value)).replaceAll('__' + name.toUpperCase() + '__', String(value)), this.labels[key]);
    }
    updateScrollControls() {
      this.querySelector('[data-comparison-scroll="-1"]').disabled = this.scroller.scrollLeft < 2;
      this.querySelector('[data-comparison-scroll="1"]').disabled = this.scroller.scrollLeft + this.scroller.clientWidth >= this.scroller.scrollWidth - 2;
    }
    toggle(handle) {
      if (!this.products.has(handle)) return;
      const index = this.selected.indexOf(handle);
      if (index >= 0) this.selected.splice(index, 1);
      else if (this.selected.length < 3) this.selected.push(handle);
      else { this.announce(this.labels.limit); return; }
      this.update();
      this.announce(this.label(index >= 0 ? 'removed_announcement' : 'added_announcement', {name: this.products.get(handle).name, count: this.selected.length}));
    }
    syncButtons() {
      document.querySelectorAll('[data-compare-handle]').forEach(button => {
        if (button.hidden) button.hidden = false;
        const handle = button.dataset.compareHandle;
        const selected = this.selected.includes(handle);
        const label = selected ? this.labels.selected_button : this.labels.button;
        if (button.getAttribute('aria-pressed') !== String(selected)) button.setAttribute('aria-pressed', String(selected));
        const text = button.querySelector('[data-compare-label]');
        if (text && text.textContent !== label) text.textContent = label;
        const icon = button.querySelector('[data-compare-icon]');
        if (icon && icon.textContent !== (selected ? '✓' : '+')) icon.textContent = selected ? '✓' : '+';
        const disabled = !this.products.has(handle) || (this.selected.length === 3 && !selected);
        if (button.disabled !== disabled) button.disabled = disabled;
        const aria = label + ' ' + (this.products.get(handle)?.name || '');
        if (button.getAttribute('aria-label') !== aria) button.setAttribute('aria-label', aria);
      });
    }
    update() {
      try { sessionStorage.setItem(this.storageKey, JSON.stringify(this.selected)); } catch {}
      this.tray.hidden = !this.selected.length;
      document.body.classList.toggle('ua-has-comparison', !!this.selected.length);
      this.querySelector('[data-comparison-count]').textContent = this.selected.length + '/3 ' + this.labels.selected;
      const chips = this.querySelector('[data-comparison-chips]');
      chips.replaceChildren(...this.selected.map(handle => {
        const product = this.products.get(handle);
        const button = this.element('button', 'ua-compare-chip', product.name + ' ×');
        button.type = 'button';
        button.dataset.comparisonRemove = handle;
        button.setAttribute('aria-label', this.labels.remove + ' ' + product.name);
        return button;
      }));
      this.querySelector('[data-comparison-open]').disabled = this.selected.length < 2;
      this.syncButtons();
      if (this.dialog.open) {
        if (this.selected.length < 2) this.dialog.close();
        else this.renderTable();
      }
    }
    renderTable() {
      const products = this.selected.map(handle => this.products.get(handle));
      const caption = this.element('caption', 'visually-hidden', this.labels.table_label);
      const head = this.element('thead');
      const header = this.element('tr');
      const corner = this.element('th', 'ua-compare-row-label', this.labels.fragrance);
      corner.scope = 'col';
      header.append(corner);
      products.forEach(product => {
        const cell = this.element('th');
        cell.scope = 'col';
        const image = this.element('img');
        image.src = product.image;
        image.alt = product.name;
        image.width = 180;
        image.height = 180;
        image.loading = 'lazy';
        cell.append(image, this.element('strong', 'ua-compare-name', product.name), this.element('span', 'ua-compare-inspiration', product.inspiration));
        const remove = this.element('button', 'ua-compare-remove', this.labels.remove);
        remove.type = 'button';
        remove.dataset.comparisonRemove = product.handle;
        remove.setAttribute('aria-label', this.labels.remove + ' ' + product.name);
        cell.append(remove);
        header.append(cell);
      });
      head.append(header);
      const body = this.element('tbody');
      const addRow = (label, content) => {
        const row = this.element('tr');
        const th = this.element('th', 'ua-compare-row-label', label);
        th.scope = 'row';
        row.append(th);
        products.forEach(product => {
          const cell = this.element('td');
          content(cell, product);
          row.append(cell);
        });
        body.append(row);
      };
      addRow(this.labels.notes, (cell, product) => { cell.textContent = product.notes || this.labels.no_notes; });
      addRow(this.labels.family, (cell, product) => { cell.textContent = product.family || this.labels.not_listed; });
      ['100ml', '5ml'].forEach(size => addRow(size === '100ml' ? this.labels.full_size : this.labels.sample, (cell, product) => {
        const variant = product.variants.find(item => item.size === size);
        if (!variant) { cell.textContent = this.labels.not_listed; return; }
        cell.append(this.element('strong', 'ua-compare-price', variant.price));
        if (!variant.available) { cell.append(this.element('span', 'ua-compare-unavailable', this.labels.unavailable)); return; }
        const link = this.element('a', size === '100ml' ? 'ua-compare-shop ua-compare-shop--primary' : 'ua-compare-shop', size === '100ml' ? this.labels.view_full_size : this.labels.view_sample);
        link.href = variant.url;
        link.setAttribute('aria-label', this.label(size === '100ml' ? 'view_full_size_label' : 'view_sample_label', {name: product.name, size}));
        cell.append(link);
      }));
      this.table.replaceChildren(caption, head, body);
      this.table.style.setProperty('--ua-compare-count', String(products.length));
      this.querySelector('.ua-compare-scroll-hint').hidden = products.length < 3;
      this.updateScrollControls();
    }
  });
}
